import { useContext, useEffect, useMemo } from 'react';
import DataContext from "../../../context/DataContext.js";
import getScaledValueFactory from "../../../utils/getScaledValueFactory.js";
import getScaleBandwidth from "../../../utils/getScaleBandwidth.js";
import getScaleBaseline from "../../../utils/getScaleBaseline.js";
import isValidNumber from "../../../typeguards/isValidNumber.js";
import { BARSERIES_EVENT_SOURCE, XYCHART_EVENT_SOURCE } from "../../../constants.js";
import useSeriesEvents from "../../../hooks/useSeriesEvents.js";
import { jsx as _jsx } from "react/jsx-runtime";
// Fallback bandwidth estimate assumes no missing data values (divides chart space by # datum)
const getFallbackBandwidth = (fullBarWidth, barPadding) =>
// clamp padding to [0, 1], bar thickness = (1-padding) * availableSpace
fullBarWidth * (1 - Math.min(1, Math.max(0, barPadding)));
function BaseBarSeries(_ref) {
  let {
    BarsComponent,
    barPadding = 0.1,
    colorAccessor,
    data,
    dataKey,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    enableEvents = true,
    xAccessor,
    xScale,
    yAccessor,
    yScale,
    ...barComponentProps
  } = _ref;
  const {
    colorScale,
    horizontal,
    theme,
    innerWidth = 0,
    innerHeight = 0
  } = useContext(DataContext);
  const getScaledX = useMemo(() => getScaledValueFactory(xScale, xAccessor), [xScale, xAccessor]);
  const getScaledY = useMemo(() => getScaledValueFactory(yScale, yAccessor), [yScale, yAccessor]);
  const scaleBandwidth = getScaleBandwidth(horizontal ? yScale : xScale);
  const barThickness = scaleBandwidth || getFallbackBandwidth((horizontal ? innerHeight : innerWidth) / data.length, barPadding);
  const xZeroPosition = useMemo(() => xScale ? getScaleBaseline(xScale) : 0, [xScale]);
  const yZeroPosition = useMemo(() => yScale ? getScaleBaseline(yScale) : 0, [yScale]);
  const color = colorScale?.(dataKey) ?? theme?.colors?.[0] ?? '#222';
  const bars = useMemo(() => {
    const xOffset = horizontal ? 0 : -barThickness / 2;
    const yOffset = horizontal ? -barThickness / 2 : 0;
    return data.map((datum, index) => {
      const x = getScaledX(datum) + xOffset;
      if (!isValidNumber(x)) return null;
      const y = getScaledY(datum) + yOffset;
      if (!isValidNumber(y)) return null;
      const barLength = horizontal ? x - xZeroPosition : y - yZeroPosition;
      if (!isValidNumber(barLength)) return null;
      return {
        key: `${index}`,
        x: horizontal ? xZeroPosition + Math.min(0, barLength) : x,
        y: horizontal ? y : yZeroPosition + Math.min(0, barLength),
        width: horizontal ? Math.abs(barLength) : barThickness,
        height: horizontal ? barThickness : Math.abs(barLength),
        fill: colorAccessor?.(datum, index) ?? color
      };
    }).filter(bar => bar);
  }, [barThickness, color, colorAccessor, data, getScaledX, getScaledY, horizontal, xZeroPosition, yZeroPosition]);
  const ownEventSourceKey = `${BARSERIES_EVENT_SOURCE}-${dataKey}`;
  const eventEmitters = useSeriesEvents({
    dataKey,
    enableEvents,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    source: ownEventSourceKey,
    allowedSources: [XYCHART_EVENT_SOURCE, ownEventSourceKey]
  });
  return /*#__PURE__*/_jsx("g", {
    className: "vx-bar-series",
    children: /*#__PURE__*/_jsx(BarsComponent, {
      bars: bars,
      horizontal: horizontal,
      xScale: xScale,
      yScale: yScale,
      ...eventEmitters,
      ...barComponentProps
    })
  });
}
export default function BaseBarSeriesWithRegisteredData(props) {
  const {
    dataKey,
    data,
    xAccessor,
    yAccessor
  } = props;
  const {
    xScale,
    yScale,
    dataRegistry
  } = useContext(DataContext);
  useEffect(() => {
    if (dataRegistry) dataRegistry.registerData({
      key: dataKey,
      data,
      xAccessor,
      yAccessor
    });
    return () => dataRegistry?.unregisterData(dataKey);
  }, [dataRegistry, dataKey, data, xAccessor, yAccessor]);
  const registryEntry = dataRegistry?.get(dataKey);

  // if scales or data are not available in context, render nothing
  if (!xScale || !yScale || !registryEntry) return null;

  // otherwise pass props + over-write data/accessors
  return /*#__PURE__*/_jsx(BaseBarSeries, {
    ...props,
    xScale: xScale,
    yScale: yScale,
    data: registryEntry.data,
    xAccessor: registryEntry.xAccessor,
    yAccessor: registryEntry.yAccessor
  });
}